import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Certification } from '../../types';
import { formatDate, toDateInput, toStringOrNull } from '../../utils/format';
import ConfirmDialog from '../common/ConfirmDialog';

interface Props {
  items: Certification[];
  onCreate: (data: Partial<Certification>) => Promise<void>;
  onUpdate: (id: Certification['id'], data: Partial<Certification>) => Promise<void>;
  onDelete: (id: Certification['id']) => Promise<void>;
}

interface FormState {
  name: string;
  issuer: string;
  issued_on: string;
  expires_on: string;
  credential_id: string;
}

const EMPTY: FormState = { name: '', issuer: '', issued_on: '', expires_on: '', credential_id: '' };

function toForm(item: Certification): FormState {
  return {
    name: item.name ?? '',
    issuer: item.issuer ?? '',
    issued_on: toDateInput(item.issued_on),
    expires_on: toDateInput(item.expires_on),
    credential_id: item.credential_id ?? '',
  };
}

const CertificationsSection: React.FC<Props> = ({ items, onCreate, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState<{ id: Certification['id'] | null; form: FormState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Certification | null>(null);
  const [deleting, setDeleting] = useState(false);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditing((s) => (s ? { ...s, form: { ...s.form, [key]: e.target.value } } : s));

  const canSave = Boolean(editing?.form.name.trim()) && !saving;

  const handleSave = async () => {
    if (!editing || !canSave) return;
    setSaving(true);
    try {
      const payload: Partial<Certification> = {
        name: editing.form.name.trim(),
        issuer: editing.form.issuer.trim(),
        issued_on: toStringOrNull(editing.form.issued_on),
        expires_on: toStringOrNull(editing.form.expires_on),
        credential_id: editing.form.credential_id.trim(),
      };
      if (editing.id === null) {
        await onCreate(payload);
      } else {
        await onUpdate(editing.id, payload);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await onDelete(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Certifications
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ id: null, form: EMPTY })}
        >
          Add certification
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No certifications added yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.issuer}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        item.issued_on ? `Issued ${formatDate(item.issued_on)}` : '',
                        item.expires_on ? `Expires ${formatDate(item.expires_on)}` : '',
                        item.credential_id ? `ID ${item.credential_id}` : '',
                      ]
                        .filter(Boolean)
                        .join(' | ')}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing({ id: item.id, form: toForm(item) })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setPendingDelete(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id === null ? 'Add certification' : 'Edit certification'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField label="Name" value={editing.form.name} onChange={set('name')} fullWidth required autoFocus />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Issuer" value={editing.form.issuer} onChange={set('issuer')} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Issued on" type="date" value={editing.form.issued_on} onChange={set('issued_on')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Expires on" type="date" value={editing.form.expires_on} onChange={set('expires_on')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Credential ID" value={editing.form.credential_id} onChange={set('credential_id')} fullWidth />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this certification?"
        message={pendingDelete ? `${pendingDelete.name} will be removed from your profile.` : undefined}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default CertificationsSection;
